import {
    Icon,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Popover,
    Tabs,
    Tab,
    Typography,
    Box,
} from "@material-ui/core";
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { getEnvVariable } from "../../../modules/environmentalList";
import { ExpandMore } from "@material-ui/icons";
import { GetData } from "../../controls/helpers/requests";

const useStyles = makeStyles((theme) => {
    return {
        root: {
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderBottom: 'none',
            boxShadow: 'none'
        },
        tabsContainer: {
            padding: '2px 8px',
            '& .MuiTab-textColorInherit': {
                opacity: '1 !important',
                color: '#ffffff !important',
                textShadow: 'none',
                filter: 'none'
            },
            '& .MuiTabs-root, & .MuiTabs-flexContainer': {
                minHeight: 'auto',
                gap: '2px'
            },
            '& .MuiTab-root': {
                minHeight: 'auto',
                padding: '3px 10px',
                minWidth: 'auto',
                maxWidth: 160,
                fontSize: '0.72rem',
                fontWeight: 500,
                textTransform: 'none',
                color: theme.palette.primary.contrastText,
                //background: 'transparent',
                borderRadius: '6px',
                margin: 0,
                transition: 'all 0.2s ease',
                '& .MuiTab-wrapper, & span': {
                    color: 'inherit',
                },
                '&:hover': {
                    background: 'rgba(255,255,255,0.1)',
                    color: '#ffffff',
                },
            },
            '& .MuiTabs-scrollButtons': {
                color: theme.palette.primary.contrastText,
                width: 28,
                '&.Mui-disabled': { opacity: 0.3 }
            },
            '& .MuiTabs-indicator': { display: 'none' }
        },
        popoverContainer: {
            width: 'auto',
            maxHeight: 450,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)'
        },
        listItem: {
            borderRadius: 6,
            margin: '2px 4px',
            padding: '6px 12px',
            transition: 'all 0.15s ease',
            '&:hover': {
                background: '#f1f5f9',
            }
        },
        selected: {
            background: '#e2e8f0 !important',
            '&:hover': {
                background: '#cbd5e1 !important',
            },
            '& .MuiListItemText-primary': {
                color: theme.palette.primary.main + ' !important',
                fontWeight: '600 !important',
            },
            '& .MuiListItemIcon-root .MuiIcon-root': {
                color: theme.palette.primary.main + ' !important',
            }
        }
    };
});

export default function SidebartabView({ isMobileMenuOpen }) {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const location = useLocation();

    const [menutabs, setMenuTabs] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openMenuIndex, setOpenMenuIndex] = useState(null);
    const [recordsAnchor, setRecordsAnchor] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentRecordMenu, setCurrentRecordMenu] = useState(null);
    const [flows, setFlows] = useState([]);

    // admin-only tabs keys - these will be used to filter out admin tabs for non-admin users
    const adminTabs = ['administration', 'my segments',  'epssettings'];

    // Load menu data and flows
    useEffect(() => {
        const loadData = async () => {
            const user = localStorage.getItem('profile') ? JSON.parse(localStorage.getItem('profile')) : null;
            if (!user) return;

            try {
                // Load menu data
                if (menutabs.length === 0) {
                    const menuUrl = getEnvVariable('System') + 'api/system/menus/UserMenu';
                    const menuData = await GetData(menuUrl);
                    
                    // Filter admin-only tabs for non-admin users
                    const isAdmin = user.isAdmin === 'Y';
                    const filteredMenuData = isAdmin 
                        ? menuData 
                        : menuData.filter(menu => !adminTabs.includes((menu.key || '').toLowerCase()));
                    
                    const sortedMenus = filteredMenuData.sort((a, b) => (a.orderNo || 999) - (b.orderNo || 999));
                    setMenuTabs(sortedMenus);
                }

                // Load flows data
                if (flows.length === 0) {
                    const flowsUrl = getEnvVariable('AdminConfig') + '/api/ProcessCategory/Active?page=0&pageSize=50';
                    const flowsData = await GetData(flowsUrl);
                    setFlows(flowsData?.data || []);
                }
            } catch (error) {
                console.error("Error loading data:", error);
            }
        };

        loadData();
    }, [menutabs.length, flows.length]);

    // Navigation handler
    const handleNavigation = useCallback(async (item) => {
        setAnchorEl(null);
        setOpenMenuIndex(null);
        setRecordsAnchor(null);

        // Handle new tab navigation
        if (item.newtab === 'Y' || item.newTab === 'Y') {
            window.open(item.link, '_blank');
            return;
        }

        // Handle local storage value navigation
        if (item.GetValforLocal) {
            const val = localStorage.getItem(item.GetValforLocal);
            const link = val ? `${item.link}${val}` : item.link;
            return history.push(link);
        }

        // Handle Process type navigation
        if (item.type === 'Process' && item.link) {
            const flowId = item.link;
            const matchingFlow = flows.find(flow => flow.FlowId === flowId || flow.id === flowId);
            
            if (matchingFlow) {
                const version = matchingFlow.Version || '1.0';
                let link = matchingFlow.link || '/userprocessflows';
                if (!link.endsWith('/')) {
                    link = `${link}/`;
                }
                link += matchingFlow.FlowId || matchingFlow.id;
                if (version) {
                    link += `/${version}`;
                }
                return history.push(link);
            } else {
                // Fallback to process flow list if flow not found
                return history.push(`/userprocessflowlist/0/${flowId}`);
            }
        }

        // Handle SubModule navigation
        if (item?.SubModuleKey) {
            const subModuleKey = item.SubModuleKey;
            const url = getEnvVariable('AdminConfig') + `/api/ProcessCategory/Published/${subModuleKey}`;
            
            try {
                const res = await GetData(url);
                if (res && res.data && Array.isArray(res.data)) {
                    if (res.data.length > 1) {
                        return history.push(`/userprocessflowlist/${subModuleKey}`);
                    } else if (res.data.length === 1) {
                        const processFlow = res.data[0];
                        const flowId = processFlow.FlowId;
                        const version = processFlow?.Version || '1.0';
                        let link = processFlow.link || '/userprocessflows';
                        if (!link.endsWith('/')) {
                            link = `${link}/`;
                        }
                        link += flowId;
                        if (version) {
                            link += `/${version}`;
                        }
                        return history.push(link);
                    } else {
                        return history.push(`/userprocessflowlist/${subModuleKey}`);
                    }
                } else {
                    return history.push(`/userprocessflowlist/${subModuleKey}`);
                }
            } catch (error) {
                console.error("Error fetching process flows:", error);
                return history.push(`/userprocessflowlist/${subModuleKey}`);
            }
        }

        // Default navigation
        if (item.link) {
            history.push(item.link);
        }
    }, [history, flows]);

    // Menu handlers
    const handleMenuOpen = useCallback((event, index) => {
        setAnchorEl(event.currentTarget);
        setOpenMenuIndex(index);
    }, []);

    const handleMenuClose = useCallback(() => {
        setAnchorEl(null);
        setOpenMenuIndex(null);
    }, []);

    const handleRecordsOpen = useCallback((event) => {
        setRecordsAnchor(event.currentTarget);
    }, []);

    const handleRecordsClose = useCallback(() => {
        setRecordsAnchor(null);
        setSearchQuery('');
        setCurrentRecordMenu(null);
    }, []);

    // Filter menu items based on admin tabs
    const getFilteredMenuItems = useCallback((items) => {
        const user = localStorage.getItem('profile') ? JSON.parse(localStorage.getItem('profile')) : null;
        if (!user || user.isAdmin === 'Y') {
            return items; // Return all items for admin users
        }
        
        // For non-admin users filter out admin-only tabs
        return items.filter(item => !adminTabs.includes((item.key || '').toLowerCase()));
    }, []);

    // Get records data
    const getRecords = useCallback((menuKey = null) => {
        const submodules = localStorage.getItem('submodule')
            ? JSON.parse(localStorage.getItem('submodule'))
            : [];

        const processed = submodules.map(sub => ({
            ...sub,
            DisplayName: getEnvVariable("AccessPortal") === 'E' && getEnvVariable("ModuleKey") !== 'APPS' && getEnvVariable("Port") !== '8553' ? "My Segments" : sub.displayName || sub.title,
            Color: sub.color || '',
            MaterialIcon: sub.materialicon || '',
            SubModuleKey: sub.key || '',
        }));

        return menuKey && menuKey !== 'Records' 
            ? processed.filter(sub => sub.key === menuKey || sub.SubModuleKey === menuKey)
            : processed;
    }, []);

    // Get filtered records for a specific menu
    const getFilteredRecordsForMenu = useCallback((menu) => {
        const records = getRecords(menu.type === 'recordmenu' ? menu.key : null);
        return records.filter(record =>
            !searchQuery ||
            (record.DisplayName || '').toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [getRecords, searchQuery]);

    // Filter records by search (for general Records menu)
    const filteredRecords = getRecords().filter(record =>
        !searchQuery ||
        (record.DisplayName || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (!menutabs.length) return null;

    return (
        <Paper square elevation={0} className={classes.root}>
            <Box className={classes.tabsContainer}>
                <Tabs
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    textColor="inherit"
                >
                    {menutabs.map((menu, index) => (
                        <Tab
                            key={menu.id || index}
                            label={
                                <Box style={{ display: 'flex', alignItems: 'center', gap: '4px',
                                     padding: (openMenuIndex === index || (recordsAnchor && currentRecordMenu && currentRecordMenu.key === menu.key)) ? '4px 6px' : undefined,
                                     border: (openMenuIndex === index || (recordsAnchor && currentRecordMenu && currentRecordMenu.key === menu.key)) ? '1px solid rgba(255,255,255,0.9)' : undefined, 
                                     borderRadius: (openMenuIndex === index || (recordsAnchor && currentRecordMenu && currentRecordMenu.key === menu.key)) ? 6 : undefined }}>
                                    <Icon style={{
                                        fontSize: 12,
                                        color: menu.colorHex || '#ffffff'
                                    }}>
                                        {menu.materialIcon}
                                    </Icon>
                                    <span style={{ fontSize: '0.75rem', color: '#ffffff' }}>
                                        {(() => {
                                            if (menu.key === 'Records' || menu.type === 'recordmenu') {
                                                const menuFilteredRecords = getFilteredRecordsForMenu(menu);
                                                if (menuFilteredRecords && menuFilteredRecords.length === 1) {
                                                    return menuFilteredRecords[0].DisplayName || menuFilteredRecords[0].FlowName || menuFilteredRecords[0].Name || menu.name;
                                                }
                                            }
                                            return menu.name;
                                        })()}
                                    </span>
                                    {menu.items && menu.items.length > 0 && menu.key !== 'Records' && menu.type !== 'recordmenu' && (
                                        <ExpandMore
                                            style={{
                                                fontSize: 12,
                                                color: 'rgba(255,255,255,0.8)',
                                                transition: 'transform 0.2s ease',
                                                transform: openMenuIndex === index ? 'rotate(180deg)' : 'none'
                                            }}
                                        />
                                    )}
                                </Box>
                            }
                            onClick={(e) => {
                                if (menu.key === 'Records' || menu.type === 'recordmenu') {
                                    const menuFilteredRecords = getFilteredRecordsForMenu(menu);
                                    if (menuFilteredRecords && menuFilteredRecords.length === 1) {
                                        handleNavigation(menuFilteredRecords[0]);
                                    } else {
                                        setCurrentRecordMenu(menu);
                                        handleRecordsOpen(e);
                                    }
                                } else if (menu.items && menu.items.length > 0) {
                                    handleMenuOpen(e, index);
                                } else {
                                    handleNavigation(menu);
                                }
                            }}
                        />
                    ))}
                </Tabs>
            </Box>

            {/* Menu Popovers */}
            {menutabs.map((menu, index) => (
                <div key={`popover-${menu.id || index}`}>
                    {/* Regular Menu Popover */}
                    {menu.items && menu.items.length > 0 && menu.key !== 'Records' && menu.type !== 'recordmenu' && (
                        <Popover
                            anchorEl={openMenuIndex === index ? anchorEl : null}
                            open={openMenuIndex === index && Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                            PaperProps={{ className: classes.popoverContainer }}
                        >
                            <List style={{ maxHeight: 400,  }}>
                                {getFilteredMenuItems(menu.items || [])
                                    .sort((a, b) => (a.orderNo || 999) - (b.orderNo || 999))
                                    .map((item, itemIndex) => (
                                        <ListItem
                                            key={itemIndex}
                                            button
                                            onClick={() => handleNavigation(item)}
                                            className={`${classes.listItem} ${item.link === location.pathname ? classes.selected : ''}`}
                                        >
                                            <ListItemIcon >
                                                <Icon style={{
                                                    fontSize: 15,
                                                    color: item.colorHex || theme.palette.primary.main
                                                }}>
                                                    {item.materialIcon}
                                                </Icon>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2" style={{
                                                        fontSize: '0.875rem',
                                                        color: item.colorHex || theme.palette.primary.main,
                                                        fontWeight: 500
                                                    }}>
                                                        {item.title}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                            </List>
                        </Popover>
                    )}
                </div>
            ))}

            {/* Records Popover */}
            <Popover
                anchorEl={recordsAnchor}
                open={Boolean(recordsAnchor)}
                onClose={handleRecordsClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{ className: classes.popoverContainer }}
            >
                <List style={{ maxHeight: 300, width: 'auto' }}>
                    {(() => {
                        const currentFilteredRecords = currentRecordMenu ? getFilteredRecordsForMenu(currentRecordMenu) : filteredRecords;
                        return currentFilteredRecords.length > 0 ? currentFilteredRecords.map((record, index) => (
                            <ListItem
                                key={index}
                                button
                                onClick={() => handleNavigation(record)}
                                className={classes.listItem}
                            >
                                <ListItemIcon >
                                    <Icon style={{
                                        fontSize: 15,
                                        color: record.Color || theme.palette.primary.main
                                    }}>
                                        {record.MaterialIcon || 'description'}
                                    </Icon>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" style={{
                                            fontSize: '0.875rem',
                                            color: record.Color || theme.palette.primary.main,
                                            fontWeight: 500                                            
                                        }}>
                                            {record.DisplayName}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        )) : (
                            <Box textAlign="center" py={2}>
                                <Typography variant="body2" style={{
                                    color: 'rgba(100,116,139,0.7)'
                                }}>
                                    No records available
                                </Typography>
                            </Box>
                        );
                    })()}
                </List>
            </Popover>
        </Paper>
    );
}